import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Bold, Italic, Heading2, List, Link2, Image as ImageIcon, Send, Loader2, Eye, Pencil, Users,
  Upload, Save, CalendarClock, Trash2, Mails,
} from "lucide-react";
import { api, BACKEND_URL, LOGO_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const shellPreview = (subject, body) => `<!doctype html><html><body style="margin:0;background:#f4f6fb;">
<table width="100%" cellpadding="0" cellspacing="0" style="background:#f4f6fb;padding:24px 12px;"><tr><td align="center">
<table width="100%" cellpadding="0" cellspacing="0" style="max-width:520px;background:#fff;border-radius:18px;overflow:hidden;border:1px solid #e7ebf3;font-family:Arial,sans-serif;">
<tr><td style="background:#0A0A0A;padding:18px 26px;"><img src="${LOGO_URL}" width="28" height="28" alt="" style="display:inline-block;vertical-align:middle;border-radius:7px;margin-right:9px;" /><span style="color:#fff;font-size:17px;font-weight:700;vertical-align:middle;">SOSConnect <span style="color:#0055FF;">AI</span></span></td></tr>
<tr><td style="padding:30px 26px;"><h1 style="margin:0 0 14px;font-size:22px;color:#111;">${subject || "Subject"}</h1><div style="font-size:15px;line-height:1.65;color:#475467;">${body || "<p>Your message…</p>"}</div></td></tr>
<tr><td style="padding:0 26px 26px;color:#98a2b3;font-size:12px;">You're receiving this because you joined SOSConnect AI. <a href="#" style="color:#98a2b3;text-decoration:underline;">Unsubscribe</a><br/>© 2026 SOSConnect AI · Help is now intelligent.</td></tr>
</table></td></tr></table></body></html>`;

const TB = [
  { cmd: "bold", icon: Bold, arg: null },
  { cmd: "italic", icon: Italic, arg: null },
  { cmd: "formatBlock", icon: Heading2, arg: "H2" },
  { cmd: "insertUnorderedList", icon: List, arg: null },
];

export default function EmailComposer({ open, onClose, onSent, preset }) {
  const editorRef = useRef(null);
  const fileRef = useRef(null);
  const [subject, setSubject] = useState("");
  const [aud, setAud] = useState({ type: "waitlist", city: "", state: "", user_type: "all" });
  const [count, setCount] = useState(null);
  const [mode, setMode] = useState("edit");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [savingTpl, setSavingTpl] = useState(false);
  const [schedule, setSchedule] = useState(false);
  const [sendAt, setSendAt] = useState("");
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const category = preset?.category || "broadcast";

  const loadTemplates = () => api.get("/admin/emails/templates").then((r) => setTemplates(r.data.templates || [])).catch(() => {});

  useEffect(() => {
    if (!open) return;
    setSubject(preset?.subject || "");
    setBody(preset?.body || "<p>Hi there,</p><p></p>");
    setAud(preset?.audience || { type: "waitlist", city: "", state: "", user_type: "all" });
    setMode("edit");
    setSchedule(false);
    setSendAt("");
    setTestTo("");
    loadTemplates();
    setTimeout(() => { if (editorRef.current) editorRef.current.innerHTML = preset?.body || "<p>Hi there,</p><p></p>"; }, 50);
  }, [open, preset]);

  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => {
      api.post("/admin/emails/audience", aud).then((r) => setCount(r.data.count)).catch(() => setCount(null));
    }, 350);
    return () => clearTimeout(id);
  }, [aud, open]);

  const exec = (cmd, arg = null) => { editorRef.current?.focus(); document.execCommand(cmd, false, arg); syncBody(); };
  const syncBody = () => setBody(editorRef.current?.innerHTML || "");
  const addLink = () => { const url = window.prompt("Link URL"); if (url) exec("createLink", url); };
  const addImage = () => { const url = window.prompt("Image URL (https://…)"); if (url) exec("insertImage", url); };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Image too large (max 8MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/emails/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const abs = `${BACKEND_URL}${r.data.url}`;
      editorRef.current?.focus();
      document.execCommand("insertImage", false, abs);
      syncBody();
      toast.success("Image added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setSubject(t.subject || "");
    setBody(t.body_html || "");
    if (editorRef.current) editorRef.current.innerHTML = t.body_html || "";
    toast.success(`Loaded "${t.name}"`);
  };

  const saveTemplate = async () => {
    const name = window.prompt("Template name");
    if (!name || !name.trim()) return;
    const html = editorRef.current?.innerHTML || body;
    setSavingTpl(true);
    try {
      await api.post("/admin/emails/templates", { name: name.trim(), subject, body_html: html });
      toast.success("Template saved");
      loadTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSavingTpl(false); }
  };

  const deleteTemplate = async (id, e) => {
    e.stopPropagation();
    try { await api.delete(`/admin/emails/templates/${id}`); loadTemplates(); toast.success("Template deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const sendTest = async () => {
    const html = editorRef.current?.innerHTML || body;
    if (!testTo.trim()) { toast.error("Enter an email to send the test to"); return; }
    if (!subject.trim()) { toast.error("Add a subject first"); return; }
    setTesting(true);
    try {
      const r = await api.post("/admin/emails/test-send", { to: testTo.trim(), subject, body_html: html });
      if (r.data.success) toast.success(`Test sent to ${r.data.to}`);
      else toast.error(`Test ${r.data.status}: ${r.data.error || ""}`);
      onSent?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
    finally { setTesting(false); }
  };

  const send = async () => {
    const html = editorRef.current?.innerHTML || body;
    if (!subject.trim()) { toast.error("Add a subject"); return; }
    if (!html || html.replace(/<[^>]*>/g, "").trim().length === 0) { toast.error("Write a message"); return; }
    if (schedule && !sendAt) { toast.error("Pick a date and time to schedule"); return; }
    setSending(true);
    try {
      if (schedule) {
        const iso = new Date(sendAt).toISOString();
        const r = await api.post("/admin/emails/schedule", { subject, body_html: html, category, audience: aud, send_at: iso });
        toast.success(`Scheduled for ${new Date(sendAt).toLocaleString()} · ~${r.data.estimated_recipients} recipients`);
      } else {
        const r = await api.post("/admin/emails/broadcast", { subject, body_html: html, category, audience: aud });
        toast.success(`Queued ${r.data.queued} email${r.data.queued === 1 ? "" : "s"}`);
      }
      onSent?.(); onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto" data-testid="email-composer">
        <DialogHeader>
          <DialogTitle>{preset?.title || "Compose email"}</DialogTitle>
          <DialogDescription>Send a fully custom message to a targeted audience. Images and formatting supported.</DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 lg:grid-cols-[1fr_1fr]">
          <div className="space-y-3">
            {/* audience */}
            <div className="rounded-xl border border-black/5 bg-[#FAFAFC] p-3">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-neutral-500"><Users className="h-3.5 w-3.5" /> Audience</p>
              <div className="grid grid-cols-2 gap-2">
                <Select value={aud.type} onValueChange={(v) => setAud((a) => ({ ...a, type: v }))}>
                  <SelectTrigger className="h-9 rounded-lg text-sm" data-testid="composer-audience-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="waitlist">Waitlist signups</SelectItem>
                    <SelectItem value="providers">Providers</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={aud.user_type} onValueChange={(v) => setAud((a) => ({ ...a, user_type: v }))} disabled={aud.type === "providers"}>
                  <SelectTrigger className="h-9 rounded-lg text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    <SelectItem value="customer">Customers</SelectItem>
                    <SelectItem value="provider">Providers</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
                <Input placeholder="City (optional)" value={aud.city} onChange={(e) => setAud((a) => ({ ...a, city: e.target.value }))} className="h-9 rounded-lg text-sm" data-testid="composer-city" />
                <Input placeholder="State (optional)" value={aud.state} onChange={(e) => setAud((a) => ({ ...a, state: e.target.value }))} className="h-9 rounded-lg text-sm" data-testid="composer-state" />
              </div>
              <p className="mt-2 text-xs font-semibold text-brand" data-testid="composer-audience-count">
                {count == null ? "Calculating…" : `${count} recipient${count === 1 ? "" : "s"} will receive this`}
              </p>
            </div>

            {/* templates */}
            <div className="flex items-center gap-2">
              <Select onValueChange={applyTemplate}>
                <SelectTrigger className="h-9 flex-1 rounded-lg text-sm" data-testid="composer-template-select">
                  <SelectValue placeholder="Load a saved template…" />
                </SelectTrigger>
                <SelectContent>
                  {templates.length === 0 && <div className="px-2 py-3 text-center text-xs text-neutral-400">No templates saved yet</div>}
                  {templates.map((t) => (
                    <div key={t.id} className="flex items-center">
                      <SelectItem value={t.id} className="flex-1">{t.name}</SelectItem>
                      <button onClick={(e) => deleteTemplate(t.id, e)} className="mr-1 grid h-7 w-7 place-items-center rounded text-neutral-400 hover:text-sos" title="Delete template" data-testid={`composer-template-delete-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" onClick={saveTemplate} disabled={savingTpl} className="h-9 gap-1.5 rounded-lg text-sm" data-testid="composer-save-template">
                {savingTpl ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save template
              </Button>
            </div>

            <Input placeholder="Subject line" value={subject} onChange={(e) => setSubject(e.target.value)} className="h-11 rounded-lg font-medium" data-testid="composer-subject" />

            {/* toolbar */}
            <div className="flex flex-wrap items-center gap-1 rounded-lg border border-black/10 bg-white p-1.5">
              {TB.map((b) => (
                <button key={b.cmd} type="button" onClick={() => exec(b.cmd, b.arg)} className="grid h-8 w-8 place-items-center rounded-md text-neutral-600 hover:bg-black/5"><b.icon className="h-4 w-4" /></button>
              ))}
              <button type="button" onClick={addLink} className="grid h-8 w-8 place-items-center rounded-md text-neutral-600 hover:bg-black/5"><Link2 className="h-4 w-4" /></button>
              <button type="button" onClick={addImage} className="grid h-8 w-8 place-items-center rounded-md text-neutral-600 hover:bg-black/5" data-testid="composer-add-image"><ImageIcon className="h-4 w-4" /></button>
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="grid h-8 w-8 place-items-center rounded-md text-neutral-600 hover:bg-black/5 disabled:opacity-50" data-testid="composer-upload-image" title="Upload image">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              </button>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={uploadImage} data-testid="composer-image-file" />
              <div className="ml-auto flex rounded-md bg-neutral-100 p-0.5">
                <button type="button" onClick={() => { syncBody(); setMode("edit"); }} className={`flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold ${mode === "edit" ? "bg-white text-brand shadow-sm" : "text-neutral-500"}`}><Pencil className="h-3 w-3" /> Edit</button>
                <button type="button" onClick={() => { syncBody(); setMode("preview"); }} className={`flex items-center gap-1 rounded px-2 py-1 text-xs font-semibold ${mode === "preview" ? "bg-white text-brand shadow-sm" : "text-neutral-500"}`} data-testid="composer-preview-toggle"><Eye className="h-3 w-3" /> Preview</button>
              </div>
            </div>

            {mode === "edit" ? (
              <div ref={editorRef} contentEditable suppressContentEditableWarning onInput={syncBody}
                className="min-h-[240px] rounded-xl border border-black/10 bg-white p-4 text-sm leading-relaxed text-neutral-800 focus:outline-none focus:ring-2 focus:ring-brand/30 [&_h2]:text-xl [&_h2]:font-bold [&_a]:text-brand [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_img]:my-2 [&_img]:max-w-full [&_img]:rounded-lg"
                data-testid="composer-editor" />
            ) : (
              <div className="min-h-[240px] overflow-hidden rounded-xl border border-black/10">
                <iframe title="Email preview" srcDoc={shellPreview(subject, body)} className="h-[300px] w-full" sandbox="" />
              </div>
            )}
          </div>

          {/* right: live email preview + delivery options */}
          <div className="space-y-3">
            <div className="hidden overflow-hidden rounded-xl border border-black/5 bg-neutral-100 lg:block">
              <div className="border-b border-black/5 bg-white px-3 py-2 text-xs font-semibold text-neutral-500">Recipient view</div>
              <iframe title="Email full preview" srcDoc={shellPreview(subject, body)} className="h-[280px] w-full" sandbox="" />
            </div>

            {/* test send */}
            <div className="rounded-xl border border-black/5 bg-[#FAFAFC] p-3">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-neutral-500"><Mails className="h-3.5 w-3.5" /> Send a test first</p>
              <div className="flex gap-2">
                <Input type="email" placeholder="you@example.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} className="h-9 flex-1 rounded-lg text-sm" data-testid="composer-test-email" />
                <Button type="button" variant="outline" onClick={sendTest} disabled={testing} className="h-9 gap-1.5 rounded-lg text-sm" data-testid="composer-test-send">
                  {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Test
                </Button>
              </div>
            </div>

            {/* schedule */}
            <div className="rounded-xl border border-black/5 bg-[#FAFAFC] p-3">
              <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-neutral-700">
                <input type="checkbox" checked={schedule} onChange={(e) => setSchedule(e.target.checked)} className="h-4 w-4 accent-brand" data-testid="composer-schedule-toggle" />
                <CalendarClock className="h-4 w-4 text-brand" /> Schedule for later
              </label>
              {schedule && (
                <Input type="datetime-local" value={sendAt} onChange={(e) => setSendAt(e.target.value)} min={new Date(Date.now() + 60000).toISOString().slice(0, 16)} className="mt-2 h-9 rounded-lg text-sm" data-testid="composer-schedule-datetime" />
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-black/5 pt-3">
          <Button variant="outline" onClick={onClose} disabled={sending} className="rounded-lg">Cancel</Button>
          <Button onClick={send} disabled={sending || (!schedule && !count)} className="gap-1.5 rounded-lg bg-brand hover:bg-brand-dark" data-testid="composer-send">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : (schedule ? <CalendarClock className="h-4 w-4" /> : <Send className="h-4 w-4" />)}
            {schedule ? "Schedule send" : `Send${count ? ` to ${count}` : ""}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
